<template>
  <div v-if="hasPermission('tag_list')">
    <v-container fluid grid-list-md>
      <v-layout row wrap justify-space-between>
        <v-flex class="pl-0">
          <tag-form @modal_closed="handleModalClosed" :model="model" @stored="fetchData"></tag-form>
        </v-flex>
        <v-flex xs3 sm3 md3 d-flex>
          <v-combobox
              clearable
              :items="[{text: trans('page.tags.diet'), value: 'diet'},
                {text: trans('page.tags.schema'), value: 'schema'},
                {text: trans('page.tags.product'), value: 'product'}
                ]"
              :label="trans('app.type')"
              v-model="filters.type"
              :return-object="true"
              hide-details
              solo
          ></v-combobox>
        </v-flex>
        <v-flex xs3 sm3 md3 d-flex>
          <v-combobox
              clearable
              :items="[{text: trans('app.own'), value: 'own'},{text: trans('app.system'), value: 'system'}]"
              :label="trans('app.database')"
              v-model="filters.database"
              :return-object="true"
              hide-details
              solo
          ></v-combobox>
        </v-flex>
        <v-flex xs3 sm3 md3 d-flex>
          <v-text-field
              v-model="filters.search"
              append-icon="search"
              :label="trans('app.search.search')"
              single-line
              hide-details
              solo
              clearable
          ></v-text-field>
        </v-flex>
      </v-layout>
    </v-container>
    <v-data-table
        :disable-initial-sort="true"
        :headers="headers"
        :items="items"
        :pagination.sync="pagination"
        :total-items="total"
        :loading="isLoading"
        :rows-per-page-items="[10,25,50,100]"
        :no-data-text="trans('app.search.no_data')"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <td>
            {{ props.item.name }}
            <icon-tooltip :tooltip-text="trans('app.tags.system_tag')" :enabled="props.item.system">
              <v-btn flat icon color="success" class="mr-0 ml-0">
                <i class="logo-small"></i>
              </v-btn>
            </icon-tooltip>
          </td>
          <td>
            {{ `page.tags.${props.item.type}` | trans }}
          </td>
          <td class="justify-end layout px-0 pr-4">

            <icon-button
                icon="edit"
                color="success"
                cls="mr-0 ml-0"
                v-if="displayEdit(props.item)"
                :permission="canEdit(props.item)"
                @click="editItem(props.item)"
                :tooltip="{top: true, bottom: false, attach: false}"
                :iconOptions="{small: true}"
            >
              {{ 'app.edit' | trans }}
            </icon-button>
            <icon-button
                icon="delete"
                color="red"
                cls="mr-0 ml-0"
                v-if="displayDelete(props.item)"
                :permission="canDelete(props.item)"
                @click="deleteItem(props.item)"
                :tooltip="{top: true, bottom: false, attach: false}"
                :iconOptions="{small: true}"
            >
              {{ 'app.delete' | trans }}
            </icon-button>
          </td>
        </tr>
      </template>
    </v-data-table>
    <div class="text-xs-center pt-2 pb-4">
      <v-pagination v-model="pagination.page" total-visible=10 :length="pages"></v-pagination>
    </div>
  </div>
  <div v-else>
    <v-container fluid>
      <v-flex xs8 offset-xs2>
        <div class="alert alert-warning">
          <span v-html="trans('app.no_access_tooltip')"></span>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>
<script>
import {trans} from '../app';
import {mapGetters} from 'vuex';
import {stringify} from "qs";
import debounce from "lodash/debounce";

export default {
  props: {
    systemOnly: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    items: [],
    model: null,
    filters: {
      search: null,
      database: null,
      type: null,
    },
    headers: [
      {
        text: trans('app.name'), align: 'left', value: 'name', sortable: true,
      },
      {
        text: trans('app.type'), align: 'left', value: 'type', sortable: true,
      },
      {
        text: '', align: 'center', value: '', sortable: false,
      },
    ],
    errors: {},
    total: 0,
    isLoading: true,
    pagination: {
      rowsPerPage: 50,
      sortBy: 'name',
      descending: false,
    },
    pages: 0,
  }),
  created() {
    this.debounceFetch = debounce(this.fetchData, 300);
  },
  mounted() {
    this.$root.$on('stored', this.debounceFetch);
    this.debounceFetch();
  },
  computed: {
    ...mapGetters(['hasPermission']),
  },
  methods: {
    editItem(item) {
      this.model = {...item};
    },
    handleModalClosed() {
      this.model = null;
    },
    async fetchData() {
      this.isLoading = true;
      const {sortBy, descending, page, rowsPerPage} = this.pagination;

      let query = {
        page: page,
        perPage: rowsPerPage,
        orderBy: sortBy,
        dir: descending ? 'desc' : 'asc',
        ...this.filters,
      };

      if (this.filters.database) {
        query.database = this.filters.database.value;
      } else if (this.systemOnly === true) {
        query.database = 'system';
      } else {
        query.database = 'own';
      }

      if (this.filters.type) {
        query.type = this.filters.type.value;
      }

      const response = await axios.get(`/v2/tags?${stringify(query, {skipNulls: true})}`);

      const {data, meta} = response.data;
      this.items = data;
      this.total = meta.total;
      this.pages = meta.last_page;
      this.isLoading = false;
    },
    deleteItem(item) {
      this.$confirmDanger(
          trans('app.confirm_removal'),
          trans('app.remove') + ' ' + trans('app.tags.tags').toLowerCase()
      ).then(async () => {
        await axios.delete('/v2/tags/' + item.id);
        this.debounceFetch();
        this.$toast(trans('app.removed'));
      }).catch((error) => {
        this.sentryCaptureException(error);
      })
    },
    displayEdit(item) {
      if (item.system) {
        return this.hasPermission('tag_system_edit');
      }
      return true;
    },
    canEdit(item) {
      if (item.system) {
        return this.hasPermission('tag_system_edit');
      }
      return this.hasPermission('tag_edit');
    },
    displayDelete(item) {
      if (item.system) {
        return this.hasPermission('tag_system_delete');
      }
      return true;
    },
    canDelete(item) {
      if (item.system) {
        return this.hasPermission('tag_system_delete');
      }
      return this.hasPermission('tag_delete');
    }
  },
  watch: {
    pagination: {
      handler(newValue, oldValue) {
        if (oldValue.page !== undefined) {
          this.debounceFetch();
        }
      },
      deep: true,
    },
    filters: {
      handler() {
        this.debounceFetch();
      },
      deep: true,
    },
  }
};
</script>
